import { formatBusinessDate, getBusinessDateKey } from "@/shared/utils/formatters";
import type { Service } from "@/features/services/types/serviceTypes";
import type { Appointment } from "../types/appointmentTypes";
import { validDateKey } from "@/shared/utils/calendarDate";

export function bookingPrefillInstant(value?: string) {
  if (!value || !validDateKey(value.slice(0, 10)) ||
    !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(value)) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function bookingPrefillSlot<T extends { startAt: Date }>(
  slots: T[], timeZone: string, instant: Date | null, time?: string,
) {
  return slots.find((slot) => instant
    ? slot.startAt.getTime() === instant.getTime()
    : !!time && formatBusinessDate(slot.startAt, timeZone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) === time);
}

// Find actual day boundaries rather than assuming every business day has 24 hours.
export function businessDayBounds(key: string, timeZone: string) {
  const date = new Date(`${key}T00:00:00Z`);
  function boundary(target: string, approximate: number) {
    let low = approximate - 36 * 3_600_000;
    let high = approximate + 36 * 3_600_000;
    while (high - low > 1) {
      const middle = Math.floor((low + high) / 2);
      if (getBusinessDateKey(new Date(middle), timeZone) < target) low = middle;
      else high = middle;
    }
    return new Date(high);
  }
  const next = new Date(date.getTime() + 86_400_000);
  return {
    startAt: boundary(key, date.getTime()),
    endAt: boundary(next.toISOString().slice(0, 10), next.getTime()),
  };
}

export function serviceBuffers(services: readonly Service[]) {
  return {
    before: Math.max(0, ...services.map((service) => service.bufferBeforeMinutes ?? 0)),
    after: Math.max(0, ...services.map((service) => service.bufferAfterMinutes ?? 0)),
  };
}

export function hasBookingConflict(
  startAt: Date, endAt: Date, before: number, after: number,
  appointments: readonly Appointment[], services: readonly Service[], locationId: string, excludeId?: string,
) {
  return appointments.some((appointment) => {
    if (appointment.id === excludeId || appointment.locationId !== locationId ||
      !["pending", "confirmed"].includes(appointment.status) || !appointment.startAt || !appointment.endAt) return false;
    const buffers = serviceBuffers(services.filter((service) => appointment.services.some((line) => line.serviceId === service.id)));
    return startAt.getTime() - before * 60_000 < appointment.endAt.getTime() + buffers.after * 60_000 &&
      endAt.getTime() + after * 60_000 > appointment.startAt.getTime() - buffers.before * 60_000;
  });
}
